import { Component, inject, OnInit, ChangeDetectionStrategy, DestroyRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DTypeForm, DTypeFormTriggerField } from '@grupo-constant/next-dtype/form';
import { NextLocator, TRIGGER_COLUMN_URI, TriggerEvent, TriggerShortcutExpr } from '@grupo-constant/next-dtype/types';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatISO, isFuture, parseISO, startOfToday } from 'date-fns';
import { cloneDeep } from 'lodash-es';
import { firstValueFrom } from 'rxjs';
import { DialogsService } from '../dialogs.service';
import { DTypeModule } from '../../core/dtype/dtype.module';
import { AvailabilityControl, AvailabilityStatusEnum, NextServicio } from '../../modules/admin/services/services.types';
import { DTypeService } from '../../core/dtype/dtype.service';
import { FormService } from '../../core/form/form.service';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { LocalizePipe } from '../../core/pipes/localize.pipe';
import { CompanyService } from '../../core/company/company.service';
import { ServiceFormsService } from '../../modules/admin/service-forms/service-forms.service';
import { ServicesService } from '../../modules/admin/services/services.service';
import { UserService } from '../../core/user/user.service';
import { FuseConfirmationDialogComponent } from '../../../@fuse/services/confirmation/dialog/dialog.component';
import { AbsenceIntervalCalendarComponent } from './absence-interval-calendar.component';

@Component({
    selector: 'app-review-absence-dialog',
    templateUrl: './review-absence-dialog.component.html',
    imports: [AbsenceIntervalCalendarComponent, DTypeModule, MatButtonModule, MatIconModule, TranslocoModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: [
        /* language=SCSS */
        `
            .review-absence-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ]
})
export class ReviewAbsenceDialogComponent implements OnInit {

    dTypeForm: DTypeForm;
    availability: AvailabilityControl;
    intervalFrom = '';
    intervalTo = '';
    intervalWeekDays: string[] = [];
    canceledDates: string[] = [];

    private companyService = inject(CompanyService);
    data: { availability: AvailabilityControl, locator: NextLocator, service: NextServicio, readonly: boolean } = inject(MAT_DIALOG_DATA);
    private dialogRef = inject(MatDialogRef<ReviewAbsenceDialogComponent>);
    private dialogsService = inject(DialogsService);
    private dTypeService = inject(DTypeService);
    private formService = inject(FormService);
    private _fuseConfirmationService = inject(FuseConfirmationService);
    private localize = inject(LocalizePipe);
    private serviceFormsService = inject(ServiceFormsService);
    private servicesService = inject(ServicesService);
    private transloco = inject(TranslocoService);
    private userService = inject(UserService);
    private destroyRef = inject(DestroyRef);

    async ngOnInit() {
        this.availability = cloneDeep(this.data.availability);
        this.canceledDates = [...(this.availability.canceledDates ?? [])].filter(day => !!day).sort();
        this.refreshInterval();
        const formData = await firstValueFrom(this.dTypeService.retrieveDtypeDataItemByUriAndOid(this.availability.formUri, this.availability.formOid));
        this.dTypeForm = await this.dTypeService.getDtypeFormByUri({
            uri: this.availability.formUri,
            locator: this.data.locator,
            bindableOptions: true
        }, formData, { readonly: true });
        const reviewCommentField = this.dTypeForm.fields['reviewComment'];
        if (reviewCommentField && !this.data.readonly) {
            reviewCommentField.hidden = false;
            reviewCommentField.readonly = false;
            reviewCommentField.control.enable();
        }
        if (this.data.service.typology === 'HOUSEKEEPING' || this.data.service.typology === 'CENTER') {
            const fromField = this.dTypeForm.fields['from'];
            if (fromField && !this.data.readonly) {
                fromField.hidden = false;
                fromField.readonly = false;
                fromField.control.enable();
            }
            const toField = this.dTypeForm.fields['to'];
            if (toField && !this.data.readonly) {
                toField.hidden = false;
                toField.readonly = false;
                toField.control.enable();
            }
            const fromTimeField = this.dTypeForm.fields['fromTime'];
            if (fromTimeField && !this.data.readonly) {
                fromTimeField.readonly = false;
                fromTimeField.control.enable();
            }
            const toTimeField = this.dTypeForm.fields['toTime'];
            if (toTimeField && !this.data.readonly) {
                toTimeField.readonly = false;
                toTimeField.control.enable();
            }
            const fullDayField = this.dTypeForm.fields['fullDay'];
            if (fullDayField && !fullDayField.hidden && !this.data.readonly) {
                fullDayField.readonly = false;
                fullDayField.control.enable();
            }
        }
        this.refreshInterval();
        for (const name of ['from', 'to', 'weekDays']) {
            this.dTypeForm.control.get(name)?.valueChanges
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.refreshInterval());
        }
    }
    
    approve() {
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('services.absences.approveConfirm'));
        confirmDialog.afterClosed().subscribe(async (confirm) => {
            if (confirm === 'confirmed') {
                let deleteTasks = false;
                if (isFuture(parseISO(this.dTypeForm.control.get('from').value))) {
                    const confirmDialog2 = this.openConfirmDialog(this.transloco.translate('services.absences.deleteTaskConfirm'), this.transloco.translate('yes'), this.transloco.translate('no'));
                    const confirmed = await firstValueFrom(confirmDialog2.afterClosed());
                    deleteTasks = confirmed === 'confirmed';
                }
                try {
                    const reply = await firstValueFrom(this.servicesService.flushAbsenceReview({
                        formOid: this.availability.formOid,
                        status: AvailabilityStatusEnum.approved,
                        comments: this.dTypeForm.control.get('reviewComment').getRawValue(),
                        deleteTasks
                    }));
                    this.closeDialog(reply.availabilityControl);
                } catch (error) {
                    console.error(error);
                    this.dialogsService.openErrorDialog({
                        message: error?.error?.echo && Object.keys(error.error.echo).length ? this.localize.transform(error.error.echo) : undefined
                    });
                }
            }
        });
    }

    markAsPending() {
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('services.absences.markAsPendingConfirm'));
        confirmDialog.afterClosed().subscribe(async (confirm) => {
            if (confirm === 'confirmed') {
                try {
                    const reply = await firstValueFrom(this.servicesService.flushAbsenceReview({
                        formOid: this.availability.formOid,
                        status: AvailabilityStatusEnum.pending,
                        comments: this.dTypeForm.control.get('reviewComment').getRawValue()
                    }));
                    this.closeDialog(reply.availabilityControl);
                } catch (error) {
                    console.error(error);
                    this.dialogsService.openErrorDialog({
                        message: error?.error?.echo && Object.keys(error.error.echo).length ? this.localize.transform(error.error.echo) : undefined
                    });
                }
            }
        });
    }

    reject() {
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('services.absences.rejectConfirm'));
        confirmDialog.afterClosed().subscribe(async (confirm) => {
            if (confirm === 'confirmed') {
                try {
                    const reply = await firstValueFrom(this.servicesService.flushAbsenceReview({
                        formOid: this.availability.formOid,
                        status: AvailabilityStatusEnum.rejected,
                        comments: this.dTypeForm.control.get('reviewComment').getRawValue()
                    }));
                    this.closeDialog(reply.availabilityControl);
                } catch (error) {
                    console.error(error);
                    this.dialogsService.openErrorDialog({
                        message: error?.error?.echo && Object.keys(error.error.echo).length ? this.localize.transform(error.error.echo) : undefined
                    });
                }
            }
        });
    }

    async edit() {
        this.dTypeForm.control.markAllAsTouched();
        if (this.dTypeForm.control.invalid) {
            this.dialogsService.openErrorDialog({ message: this.transloco.translate('errorsInForm') });
            return;
        }
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('services.absences.saveConfirm'));
        const confirm = await firstValueFrom(confirmDialog.afterClosed());
        if (confirm === 'confirmed') {
            try {
                await this.fillTriggerFields([
                    TriggerEvent.ON_QUEUE,
                    TriggerEvent.ON_QUEUE_RESET,
                    TriggerEvent.ON_SENDING]);
                const formData = this.dTypeForm.control.getRawValue();
                await firstValueFrom(this.formService.flushFormData({
                    data: formData,
                    serviceId: this.data.locator.serviceId,
                    uri: this.data.availability.formUri,
                }));
                const availability = this.data.availability;
                availability.canceledDates = this.canceledDatesInside();
                availability.from = formData.from;
                availability.to = formData.to;
                availability.fromTime = formData.fromTime;
                availability.toTime = formData.toTime;
                this.closeDialog(availability);
            } catch (error) {
                console.error(error);
                this.dialogsService.openErrorDialog({
                    message: error?.echo && Object.keys(error.echo).length ? this.localize.transform(error.echo) : undefined
                });
            }
        }
    }

    async remove() {
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('services.absences.deleteConfirm'));
        const confirm = await firstValueFrom(confirmDialog.afterClosed());
        if (confirm === 'confirmed') {
            try {
                await firstValueFrom(this.serviceFormsService.deleteForm({
                    oid: this.data.availability.formOid,
                    uri: this.data.availability.formUri,
                }));
                this.closeDialog(undefined, true);
            } catch (error) {
                console.error(error);
                this.dialogsService.openErrorDialog({
                    message: error?.echo && Object.keys(error.echo).length ? this.localize.transform(error.echo) : undefined
                });
            }
        }
    }

    private refreshInterval() {
        this.intervalFrom = this.isoDate(this.dTypeForm?.control.get('from')?.value) || this.isoDate(this.availability?.from);
        const to = this.isoDate(this.dTypeForm?.control.get('to')?.value);
        this.intervalTo = to || this.intervalFrom;
        const weekDays = this.dTypeForm?.control.get('weekDays')?.value ?? this.availability?.weekDays;
        this.intervalWeekDays = Array.isArray(weekDays) ? [...weekDays] : [];
    }

    private canceledDatesInside(): string[] {
        const from = this.isoDate(this.dTypeForm?.control.get('from')?.value) || this.isoDate(this.availability?.from);
        if (!from) {
            return [...this.canceledDates];
        }
        const to = this.isoDate(this.dTypeForm?.control.get('to')?.value) || from;
        const end = to < from ? from : to;
        return this.canceledDates.filter(day => day >= from && day <= end);
    }

    private isoDate(value: unknown): string {
        return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.substring(0, 10) : '';
    }

    closeDialog(availability?: AvailabilityControl, remove = false): void {
        this.dialogRef.close({ availability, remove });
    }

    private async fillTriggerFields(event: TriggerEvent[]) {
        for (const field of Object.values(this.dTypeForm.fields)
            .filter(f => f.type === TRIGGER_COLUMN_URI)) {
            const needsToBeFilled = this.formService.checkTriggerField({
                value: this.dTypeForm.control.get(field.name).value,
                triggerField: (field as DTypeFormTriggerField),
                event
            });

            if (needsToBeFilled) {
                this.dTypeForm.control.get(field.name)
                    .setValue(await this.getTriggerFieldValue((field as DTypeFormTriggerField)));
            }
        }
    }
    
    private async getTriggerFieldValue(triggerField: DTypeFormTriggerField) {
        let value: any = null;
        switch (triggerField.shortcutExpr) {
            case TriggerShortcutExpr.userId:
                const user = await firstValueFrom(this.userService.get());
                value = user?.userId;
                break;
            case TriggerShortcutExpr.companyId:
                const company = await firstValueFrom(this.companyService.get());
                value = company.companyId;
                break;
            case TriggerShortcutExpr.ISODatetime:
            case TriggerShortcutExpr.ISOTime:
                value = formatISO(new Date());
                break;
            case TriggerShortcutExpr.ISODate:
                value = formatISO(startOfToday());
                break;
            case TriggerShortcutExpr.serviceId:
                value = this.data.locator.serviceId;
                break;
            case TriggerShortcutExpr.formUri:
                value = this.dTypeForm.dType.uri;
                break;
            default:
                break;
        }
        return value;
    }

    private openConfirmDialog(message: string, confirmLabel?: string, cancelLabel?: string): MatDialogRef<FuseConfirmationDialogComponent, any> {
        return this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: confirmLabel ? confirmLabel : this.transloco.translate('confirm')
                },
                cancel: {
                    label: cancelLabel ? cancelLabel : this.transloco.translate('cancel')
                }
            }
        });
    }

}
